import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Check, X, Zap, Crown, Star, ChevronDown, ChevronUp,
  Users, Trophy, BookOpen, Target, TrendingUp, Clock,
  Shield, ArrowRight, BarChart2, Brain, Medal, Flame,
  Mail, CheckCircle, AlertCircle
} from 'lucide-react';

import { BACKEND_URL } from '../config';

// ─── Countdown Timer ────────────────────────────────────────────────────────
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState({});
  useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) return setTimeLeft({ h: '00', m: '00', s: '00' });
      const h = String(Math.floor((diff / 1000 / 60 / 60) % 24)).padStart(2, '0');
      const m = String(Math.floor((diff / 1000 / 60) % 60)).padStart(2, '0');
      const s = String(Math.floor((diff / 1000) % 60)).padStart(2, '0');
      setTimeLeft({ h, m, s });
    };
    calc();
    const id = setInterval(calc, 1000);
    return () => clearInterval(id);
  }, [targetDate]);
  return timeLeft;
}

// Sale ends March 31 midnight
const SALE_END = new Date('2026-03-31T23:59:59');

// CUET exam approx May 15
const CUET_DATE = new Date('2026-05-15T00:00:00');

function DaysToExam() {
  const days = Math.ceil((CUET_DATE - new Date()) / (1000 * 60 * 60 * 24));
  return days > 0 ? days : 0;
}

// ─── Cashfree loader ─────────────────────────────────────────────────────────
const loadCashfree = () =>
  new Promise((resolve) => {
    if (window.Cashfree) return resolve(new window.Cashfree({ mode: 'production' }));
    const s = document.createElement('script');
    s.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    s.onload = () => resolve(new window.Cashfree({ mode: 'production' }));
    document.head.appendChild(s);
  });

// ─── Plans data ──────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'free',
    name: 'Starter',
    tagline: 'Try before you commit',
    price: '₹0',
    original: null,
    period: 'forever',
    highlight: false,
    badge: null,
    cta: 'Start Free',
    color: 'border-gray-200',
    ctaClass: 'bg-gray-900 hover:bg-gray-800 text-white',
    features: [
      { text: '1 full-length mock test (any subject)', included: true },
      { text: 'Score report after test', included: true },
      { text: 'Leaderboard — view your rank', included: true },
      { text: 'AI-generated questions', included: true },
      { text: 'Weak-area breakdown', included: false },
      { text: 'AI-powered feedback', included: false },
      { text: 'Rank prediction', included: false },
      { text: 'Compare with toppers', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For serious CUET aspirants',
    price: '₹699',
    original: '₹999',
    period: '/month',
    highlight: true,
    badge: 'Most Popular',
    cta: 'Upgrade to Pro',
    color: 'border-blue-500',
    ctaClass: 'bg-blue-600 hover:bg-blue-700 text-white',
    features: [
      { text: '8 full-length mocks per subject/month', included: true },
      { text: 'Up to 40 mocks across 5 subjects', included: true },
      { text: 'Full national leaderboard + rank badge', included: true },
      { text: 'AI weak-area analysis & heatmap', included: true },
      { text: 'Question-by-question AI feedback', included: true },
      { text: 'Topic-wise performance reports', included: true },
      { text: 'Rank prediction', included: false },
      { text: 'Compare with toppers', included: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'For top 1% rank seekers',
    price: '₹999',
    original: '₹1,499',
    period: '/month',
    highlight: false,
    badge: 'Best Value',
    cta: 'Go Premium',
    color: 'border-amber-400',
    ctaClass: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white',
    features: [
      { text: '10 full-length mocks per subject/month', included: true },
      { text: 'Up to 50 mocks across 5 subjects', included: true },
      { text: 'Full national leaderboard + rank badge', included: true },
      { text: 'AI weak-area analysis & heatmap', included: true },
      { text: 'Question-by-question AI feedback', included: true },
      { text: 'Topic-wise performance reports', included: true },
      { text: 'College rank prediction (SRCC, St. Stephen\'s etc.)', included: true },
      { text: 'Compare your answers with toppers', included: true },
    ],
  },
];

const FAQS = [
  {
    q: 'Is the free mock really free? No credit card needed?',
    a: 'Absolutely. Sign up, select your subjects, and start your free mock immediately — no payment info required. You get 1 full-length, 50-question AI-generated mock test completely free.',
  },
  {
    q: 'How are mock tests generated? Are they exam-accurate?',
    a: 'Our mocks are generated by Gemini AI, trained on past CUET papers and NTA guidelines. Each test follows the official pattern: 50 questions, 60 minutes, +5/-1 marking. Questions are refreshed every time so you never repeat the same paper.',
  },
  {
    q: 'Can I switch subjects after paying?',
    a: 'Yes. After onboarding, you can pick any 5 subjects. Pro plan gives you 8 mocks per subject per month. If you focus on 3 subjects instead of 5, you still get the same mocks for those subjects.',
  },
  {
    q: 'How does the leaderboard work?',
    a: 'After each mock, your score is ranked against every student who has attempted that same mock. You get a live national percentile. Free users can view the leaderboard; Pro & Premium users get a ranked badge displayed on their profile.',
  },
  {
    q: 'What happens after 30 days? Does it auto-renew?',
    a: 'Subscriptions are manual — they do NOT auto-renew. When your 30 days are up, your account moves back to Free plan. You\'ll get a reminder email before expiry and can renew anytime.',
  },
  {
    q: 'Can I get a refund if I\'m not satisfied?',
    a: 'All purchases are final as per our refund policy. However, if you face any technical issues that prevented you from using the platform, write to us at info@crackcuet.com and we\'ll review your case.',
  },
  {
    q: 'I\'m taking CUET for 6 subjects. Which plan fits?',
    a: 'During onboarding you select your 5 subjects (3 domain + English + General Aptitude Test). All mocks are covered within your selected subjects. Premium at ₹999/month gives you the most coverage per rupee.',
  },
  {
    q: 'Is Crack CUET safe to pay on? How is payment processed?',
    a: 'Payments are processed via Cashfree — India\'s leading payment gateway, trusted by Nykaa, Dream11, and thousands more. Your card details never touch our servers. 100% PCI-DSS compliant.',
  },
];

// ─── FAQ Item ────────────────────────────────────────────────────────────────
function FAQItem({ q, a, isOpen, onToggle }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        <span className="font-semibold text-gray-900 pr-4">{q}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-blue-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-5 pb-5 text-gray-600 leading-relaxed bg-white border-t border-gray-100">
          {a}
        </div>
      )}
    </div>
  );
}

// ─── Stat Badge ──────────────────────────────────────────────────────────────
function Stat({ icon: Icon, value, label, color }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="font-bold text-gray-900 text-lg leading-tight">{value}</p>
        <p className="text-gray-500 text-xs">{label}</p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PricingLandingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [payError, setPayError] = useState(null);
  const [openFAQ, setOpenFAQ] = useState(0);
  const countdown = useCountdown(SALE_END);
  const daysLeft = DaysToExam();
  const pricingRef = useRef(null);

  const scrollToPricing = () => pricingRef.current?.scrollIntoView({ behavior: 'smooth' });

  const handleCTA = async (planId) => {
    if (planId === 'free') {
      navigate(user ? '/dashboard' : '/auth');
      return;
    }
    // Frictionless direct checkout for paid plans when not logged in
    if (!user || !token) {
      navigate(`/checkout?plan=${planId}`);
      return;
    }
    setPayError(null);
    setLoadingPlan(planId);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/create-order`,
        { plan: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        const { payment_session_id } = response.data.data;
        const cashfree = await loadCashfree();
        cashfree.checkout({ paymentSessionId: payment_session_id, redirectTarget: '_self' });
      } else {
        setPayError(response.data.message || 'Failed to initiate payment. Please try again.');
        setLoadingPlan(null);
      }
    } catch (err) {
      setPayError('Payment failed. Please try again or contact info@crackcuet.com');
      setLoadingPlan(null);
    }
  };

  const alreadyPro = user?.plan === 'pro';
  const alreadyPremium = user?.plan === 'premium';

  return (
    <div className="min-h-screen bg-white font-sans">

      {/* ── Announcement Bar ─────────────────────────────────────── */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 text-white py-2.5 px-4 text-center text-sm font-medium">
        <span className="opacity-90">Limited Time:</span>{' '}
        <span className="font-bold">30% OFF on Pro &amp; Premium</span>
        <span className="mx-3 opacity-50">|</span>
        Offer ends in{' '}
        <span className="font-mono font-bold bg-white/20 px-2 py-0.5 rounded-md ml-1">
          {countdown.h}:{countdown.m}:{countdown.s}
        </span>
      </div>

      {/* ── Nav Bar ──────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-gray-900">Crack CUET</span>
          </button>
          <div className="flex items-center gap-3">
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-blue-600 font-medium hover:underline"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/auth')}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Log in
                </button>
                <button
                  onClick={scrollToPricing}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-full font-semibold transition-colors"
                >
                  See Plans
                </button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white overflow-hidden relative">
        {/* Background texture */}
        <div className="absolute inset-0 opacity-5" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px'}} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 text-center">
          {/* Exam urgency pill */}
          <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-400/30 text-red-300 px-4 py-1.5 rounded-full text-sm font-medium mb-8">
            <Flame className="h-4 w-4" />
            CUET 2026 is approximately <strong className="text-red-200">{daysLeft} days away</strong>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            India's Smartest
            <span className="block text-blue-400">CUET Mock Platform</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-10 leading-relaxed">
            AI-generated mocks that mirror real CUET papers. Practice smarter, rank higher,
            and walk into exam day with <span className="text-white font-semibold">confidence</span>.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="hero-start-free-btn"
              onClick={() => navigate(user ? '/dashboard' : '/auth')}
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg hover:shadow-xl"
            >
              Start for Free
            </button>
            <button
              data-testid="hero-see-plans-btn"
              onClick={scrollToPricing}
              className="bg-blue-600/20 border border-blue-400/40 hover:bg-blue-600/30 text-blue-200 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all"
            >
              View Plans
              <ArrowRight className="inline-block h-5 w-5 ml-2" />
            </button>
          </div>
        </div>
      </section>

      {/* ── Social Proof Bar ─────────────────────────────────────── */}
      <section className="bg-white border-b border-gray-100 py-8">
        <div className="max-w-5xl mx-auto px-4 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <Stat icon={Users} value="460+" label="Active students" color="bg-blue-500" />
          <Stat icon={BookOpen} value="240+" label="AI-generated mock tests" color="bg-green-500" />
          <Stat icon={Trophy} value="100%" label="Reported score improvement" color="bg-amber-500" />
          <Stat icon={Target} value="15 Subjects" label="Covered across domains" color="bg-purple-500" />
        </div>
      </section>

      {/* ── How it compares ──────────────────────────────────────── */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Why Crack CUET?</p>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-10">
            What makes us different
          </h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                icon: Brain,
                title: 'AI-Powered, Not Static',
                desc: 'Every mock is freshly generated by Gemini AI — no recycled question banks. Every test feels new.',
                color: 'text-blue-600 bg-blue-50',
              },
              {
                icon: BarChart2,
                title: 'National Percentile, Live',
                desc: 'See exactly where you rank among thousands of students the moment you submit your test.',
                color: 'text-green-600 bg-green-50',
              },
              {
                icon: TrendingUp,
                title: 'Weak-Area Targeting',
                desc: 'Our AI maps exactly which topics are costing you marks — and shows you how to fix them.',
                color: 'text-purple-600 bg-purple-50',
              },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-left">
                <div className={`w-12 h-12 rounded-xl ${f.color} flex items-center justify-center mb-4`}>
                  <f.icon className="h-6 w-6" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Cards ────────────────────────────────────────── */}
      <section ref={pricingRef} className="py-20 bg-white" id="pricing">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">
              No hidden charges. No auto-renewals. Pay for exactly what you need.
            </p>
            {/* Offer countdown */}
            <div className="inline-flex items-center gap-3 mt-6 bg-orange-50 border border-orange-200 rounded-full px-5 py-2">
              <Clock className="h-4 w-4 text-orange-500" />
              <span className="text-sm text-orange-800 font-medium">
                30% off ends in{' '}
                <span className="font-mono font-bold text-orange-600">
                  {countdown.h}h {countdown.m}m {countdown.s}s
                </span>
              </span>
            </div>
          </div>

          {payError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 max-w-xl mx-auto">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{payError}</p>
            </div>
          )}

          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((plan) => {
              const isCurrentPlan =
                (plan.id === 'pro' && alreadyPro) ||
                (plan.id === 'premium' && alreadyPremium);

              return (
                <div
                  key={plan.id}
                  data-testid={`pricing-card-${plan.id}`}
                  className={`relative rounded-2xl border-2 ${plan.color} ${
                    plan.highlight
                      ? 'shadow-2xl shadow-blue-100 scale-[1.02] bg-blue-600 text-white'
                      : 'bg-white shadow-sm'
                  } p-6 flex flex-col`}
                >
                  {plan.badge && (
                    <div
                      className={`absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-bold shadow-md ${
                        plan.highlight
                          ? 'bg-white text-blue-600'
                          : 'bg-amber-500 text-white'
                      }`}
                    >
                      {plan.badge}
                    </div>
                  )}

                  <div className="mb-6">
                    <p className={`text-sm font-semibold mb-1 ${plan.highlight ? 'text-blue-200' : 'text-gray-500'}`}>
                      {plan.name}
                    </p>
                    <p className={`text-xs mb-4 ${plan.highlight ? 'text-blue-200' : 'text-gray-400'}`}>
                      {plan.tagline}
                    </p>
                    <div className="flex items-end gap-2">
                      <span className={`text-5xl font-extrabold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                        {plan.price}
                      </span>
                      <div className="mb-2">
                        {plan.original && (
                          <span className={`line-through text-sm block ${plan.highlight ? 'text-blue-300' : 'text-gray-400'}`}>
                            {plan.original}
                          </span>
                        )}
                        <span className={`text-sm ${plan.highlight ? 'text-blue-200' : 'text-gray-500'}`}>
                          {plan.period}
                        </span>
                      </div>
                    </div>
                    {plan.original && (
                      <span className={`inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded-full ${
                        plan.highlight ? 'bg-green-400 text-green-900' : 'bg-green-100 text-green-700'
                      }`}>
                        You save {plan.id === 'pro' ? '₹300' : '₹500'}/month
                      </span>
                    )}
                  </div>

                  <ul className="space-y-3 flex-1 mb-8">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm">
                        {f.included ? (
                          <CheckCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-green-300' : 'text-green-500'}`} />
                        ) : (
                          <X className={`h-4 w-4 mt-0.5 flex-shrink-0 ${plan.highlight ? 'text-blue-400' : 'text-gray-300'}`} />
                        )}
                        <span className={f.included ? (plan.highlight ? 'text-white' : 'text-gray-700') : (plan.highlight ? 'text-blue-300' : 'text-gray-400')}>
                          {f.text}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`cta-btn-${plan.id}`}
                    onClick={() => handleCTA(plan.id)}
                    disabled={loadingPlan === plan.id || isCurrentPlan}
                    className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all ${
                      isCurrentPlan
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : plan.highlight
                        ? 'bg-white text-blue-700 hover:bg-blue-50 shadow-lg'
                        : plan.ctaClass
                    } disabled:opacity-70`}
                  >
                    {loadingPlan === plan.id ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : isCurrentPlan ? (
                      <span className="flex items-center justify-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        Current Plan
                      </span>
                    ) : (
                      plan.cta
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <p className="text-center text-xs text-gray-400 mt-6">
            All plans are one-time manual payments. No auto-renewals. Secured by Cashfree.
          </p>
        </div>
      </section>

      {/* ── Feature Deep Dive ────────────────────────────────────── */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">What You Get</p>
            <h2 className="text-3xl font-extrabold text-gray-900">Everything your CUET prep needs</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: BookOpen, color: 'bg-blue-500',
                title: 'Exam-Pattern Mocks',
                desc: '50 questions, 60 minutes, +5/-1 marking — identical to official CUET format. No shortcuts.',
              },
              {
                icon: Brain, color: 'bg-purple-500',
                title: 'Fresh AI Questions Every Time',
                desc: 'Powered by Gemini AI. New questions each mock — no repeats, no memorisation tricks.',
              },
              {
                icon: BarChart2, color: 'bg-green-500',
                title: 'Live National Leaderboard',
                desc: 'Real-time rank among actual CUET aspirants. Know your percentile the moment you submit.',
              },
              {
                icon: Target, color: 'bg-red-500',
                title: 'Weak-Area Heatmap',
                desc: 'Visual breakdown of which chapters and topics are dragging your score. Fix what matters.',
              },
              {
                icon: Medal, color: 'bg-amber-500',
                title: 'College Rank Prediction',
                desc: 'Based on your score and cutoffs, see which colleges you\'re on track for. (Premium only)',
              },
              {
                icon: Shield, color: 'bg-slate-600',
                title: 'Zero Auto-Renewal',
                desc: 'We\'ll never charge you without your consent. Renew only when you want to.',
              },
            ].map((f) => (
              <div key={f.title} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex gap-4">
                <div className={`w-10 h-10 ${f.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <f.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1 text-sm">{f.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ─────────────────────────────────────────── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">Student Stories</p>
            <h2 className="text-3xl font-extrabold text-gray-900">Toppers trust Crack CUET</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              {
                name: 'Ananya S.',
                college: 'SRCC, DU — Rank 47',
                text: 'I was scoring 65% in self-study. After 3 weeks of Crack CUET mocks, I jumped to 84%. The AI feedback literally pointed out my weak chapters.',
              },
              {
                name: 'Rohan M.',
                college: 'Hindu College, DU — 99.2 percentile',
                text: 'The leaderboard kept me honest. I knew exactly how I compared to students across India. That competitive pressure pushed me every day.',
              },
              {
                name: 'Priya K.',
                college: 'LSR, DU — 98.8 percentile',
                text: 'Best ₹699 I spent for my CUET prep. The variety of questions per mock is amazing — never felt like I was repeating the same paper.',
              },
            ].map((t) => (
              <div key={t.name} className="bg-slate-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex text-amber-400 mb-3">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                </div>
                <p className="text-gray-700 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                  <p className="text-xs text-blue-600">{t.college}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────── */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-blue-600 mb-3">FAQ</p>
            <h2 className="text-3xl font-extrabold text-gray-900">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <FAQItem
                key={i}
                q={faq.q}
                a={faq.a}
                isOpen={openFAQ === i}
                onToggle={() => setOpenFAQ(openFAQ === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────────── */}
      <section className="py-20 bg-gradient-to-br from-blue-700 to-blue-900 text-white text-center">
        <div className="max-w-3xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-1.5 rounded-full text-sm mb-6">
            <Flame className="h-4 w-4 text-orange-300" />
            <span>{daysLeft} days left to CUET — every mock counts</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
            Ready to crack CUET?
          </h2>
          <p className="text-blue-200 text-lg mb-8 max-w-xl mx-auto">
            Join 460+ students already using Crack CUET. Start free today.
            No card required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              data-testid="final-cta-free-btn"
              onClick={() => navigate(user ? '/dashboard' : '/auth')}
              className="bg-white text-blue-700 hover:bg-blue-50 px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg"
            >
              Start Free — No Card Needed
            </button>
            <button
              data-testid="final-cta-pro-btn"
              onClick={() => handleCTA('pro')}
              disabled={loadingPlan === 'pro' || alreadyPro}
              className="bg-blue-500 hover:bg-blue-400 border border-blue-400 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all disabled:opacity-70"
            >
              {loadingPlan === 'pro' ? 'Processing...' : 'Upgrade to Pro — ₹699/mo'}
            </button>
          </div>
          <p className="text-blue-300 text-sm mt-6">
            Have questions?{' '}
            <a href="mailto:info@crackcuet.com" className="text-white underline hover:no-underline">
              info@crackcuet.com
            </a>
          </p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <footer className="bg-slate-950 text-slate-400 py-8">
        <div className="max-w-5xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center">
              <BookOpen className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="text-white font-semibold">Crack CUET</span>
            <span className="text-slate-600">|</span>
            <span>© 2026 All rights reserved</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/privacy-policy')} className="hover:text-white transition-colors">Privacy</button>
            <button onClick={() => navigate('/refund-policy')} className="hover:text-white transition-colors">Refunds</button>
            <button onClick={() => navigate('/terms')} className="hover:text-white transition-colors">Terms</button>
            <a href="mailto:info@crackcuet.com" className="flex items-center gap-1 hover:text-white transition-colors">
              <Mail className="h-3.5 w-3.5" /> info@crackcuet.com
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
}
